import { memo, useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import classNames from './styles.module.css';

export interface SlideProps {
  id?: string;
  children: React.ReactNode;
  meta?: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
  };
}

const Slide = memo(function Slide(props: SlideProps) {
  const { id, children, meta } = props;
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0.5);
      },
      { threshold: [0.5] },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <article
      ref={ref}
      id={`slide-${id}`}
      className={classNames.container}
      role="region"
      aria-label={id ? `${id} section` : 'Introduction section'}
    >
      {meta && isVisible && (
        <Helmet>
          {meta.title && <title>{meta.title}</title>}
          {meta.description && <meta name="description" content={meta.description} />}
          {/* Always use base URL as canonical to avoid duplicate content issues with hash routing */}
          <link rel="canonical" href="https://jpholt.dev/" />
          <meta
            name="robots"
            content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          />
          {meta.image && <meta property="og:image" content={meta.image} />}
          {meta.title && <meta property="og:title" content={meta.title} />}
          {meta.description && <meta property="og:description" content={meta.description} />}
          {meta.title && <meta name="twitter:title" content={meta.title} />}
          {meta.description && <meta name="twitter:description" content={meta.description} />}
          {meta.image && <meta name="twitter:image" content={meta.image} />}

          {/* Per-page JSON-LD: WebPage + BreadcrumbList */}
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'WebPage',
                  name: meta.title,
                  description: meta.description,
                  url: meta.url || typeof window !== 'undefined' ? window.location.href : undefined,
                },
                {
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: 'Home',
                      item: 'https://jpholt.dev/',
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: meta.title || 'Page',
                      item:
                        meta.url ||
                        (typeof window !== 'undefined'
                          ? window.location.href
                          : 'https://jpholt.dev/'),
                    },
                  ],
                },
              ],
            })}
          </script>
        </Helmet>
      )}
      {children}
    </article>
  );
});

export default Slide;
